// src/pages/Users.jsx
// Full user management: list + create + edit + deactivate
// Accessible only to ryts=1 (Admin/Leader)

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users as UsersApi } from '../services/api';
import { useAuth, ROLES } from '../context/AuthContext';
import { Company } from '../services/api';

// ── Role labels matching your PHP ryts ENUM ───────────────────
const ROLE_MAP = {
    '1': { label: 'Admin / Leader',      color: '#1a3a6e', bg: '#dbeafe' },
    '2': { label: 'Quotation Builder',   color: '#166534', bg: '#dcfce7' },
    '3': { label: 'Manager',             color: '#92400e', bg: '#fef3c7' },
};

const DESIGNATIONS = [
    'Sales and Service of Automation Products',
    'Branch Manager',
    'Assistant sales managers',
    'Sales Support',
    'Sales Executive',
    'Sales Manager',
    'Proprietor',
    'Partner',
    'Automation Manager',
    'Accounts Department',
    'DGM',
    'AGM',
    'HR Manager',
];

const emptyForm = {
    id: 0, name: '', email: '', phone: '',
    designation: '', ryts: '2', password: '', confirmPassword: '', location_id:'',
};

export default function UsersPage() {
    const { user }   = useAuth();
    const navigate   = useNavigate();
    const [locations, setLocations] = useState([]);
    // ── Guard: only admin ─────────────────────────────────────
    useEffect(() => {
        if (user.ryts !== '1' && user.ryts !== 1) {
            navigate('/');
        }
    }, [user, navigate]);

    const [users,     setUsers]     = useState([]);
    const [busy,      setBusy]      = useState(true);
    const [search,    setSearch]    = useState('');
    const [showForm,  setShowForm]  = useState(false);
    const [form,      setForm]      = useState(emptyForm);
    const [saving,    setSaving]    = useState(false);
    const [err,       setErr]       = useState('');
    const [success,   setSuccess]   = useState('');
    const [showPwdFor, setShowPwdFor] = useState(null); // user id for pwd reset modal
    const [newPwd,    setNewPwd]    = useState('');
    const [pwdSaving, setPwdSaving] = useState(false);
    useEffect(() => {
        Company.addresses().then(setLocations);
    }, []);
    // ── Load users ────────────────────────────────────────────
    const load = useCallback(() => {
        setBusy(true);
        UsersApi.list()
            .then(res => setUsers(res.data || []))
            .catch(e  => setErr(e.message))
            .finally(() => setBusy(false));
    }, []);
    useEffect(load, [load]);

    // ── Filtered list ─────────────────────────────────────────
    const filtered = users.filter(u =>
        !search ||
        u.name?.toLowerCase().includes(search.toLowerCase()) ||
        u.email?.toLowerCase().includes(search.toLowerCase()) ||
        u.designation?.toLowerCase().includes(search.toLowerCase())
    );

    // ── Open form ─────────────────────────────────────────────
    const openCreate = () => {
        setForm(emptyForm);
        setErr('');
        setShowForm(true);
    };
    const openEdit = (u) => {
        setForm({
            id: u.id, name: u.name, email: u.email,
            phone: u.phone || '', designation: u.designation || '',
            ryts: String(u.ryts), password: '', confirmPassword: '', location_id: u.location_id || '', 
        });
        setErr('');
        setShowForm(true);
    };

    // ── Save (create / update) ────────────────────────────────
    const handleSave = async (e) => {
        e.preventDefault();
        setErr('');

        if (!form.name || !form.email || !form.designation) {
            setErr('Name, email and designation are required.'); return;
        }
        if (form.id === 0 && form.password.length < 6) {
            setErr('Password must be at least 6 characters.'); return;
        }
        if (form.password && form.password !== form.confirmPassword) {
            setErr('Passwords do not match.'); return;
        }

        setSaving(true);
        try {
            const payload = { ...form };
            delete payload.confirmPassword;
            if (!payload.password) delete payload.password; // don't send empty on edit

            const res = await UsersApi.save(payload);
            if (res.error) { setErr(res.error); return; }

            setSuccess(form.id === 0 ? 'User created successfully!' : 'User updated successfully!');
            setShowForm(false);
            load();
            setTimeout(() => setSuccess(''), 4000);
        } catch (ex) {
            setErr(ex.message);
        } finally {
            setSaving(false);
        }
    };

    // ── Toggle active ─────────────────────────────────────────
    const toggleActive = async (u) => {
        const action = u.active == 1 ? 'deactivate' : 'reactivate';
        if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} ${u.name}?`)) return;
        try {
            const res = await UsersApi.setActive(u.id, u.active == 1 ? 0 : 1);
            if (res.error) { setErr(res.error); return; }
            setSuccess(`User ${action}d.`);
            load();
            setTimeout(() => setSuccess(''), 3000);
        } catch (ex) {
            setErr(ex.message);
        }
    };

    // ── Reset password ────────────────────────────────────────
    const handlePwdReset = async () => {
        if (newPwd.length < 6) { setErr('Password must be at least 6 characters.'); return; }
        setPwdSaving(true);
        try {
            const res = await UsersApi.resetPassword(showPwdFor, newPwd);
            if (res.error) { setErr(res.error); return; }
            setSuccess('Password reset successfully.');
            setShowPwdFor(null);
            setNewPwd('');
            setTimeout(() => setSuccess(''), 3000);
        } catch (ex) {
            setErr(ex.message);
        } finally {
            setPwdSaving(false);
        }
    };

    const f = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

    // ── UI helpers ────────────────────────────────────────────
    const Badge = ({ ryts }) => {
        const r = ROLE_MAP[String(ryts)] || { label: ryts, color: '#555', bg: '#eee' };
        return (
            <span style={{
                background: r.bg, color: r.color,
                padding: '2px 9px', borderRadius: 12,
                fontSize: '.72rem', fontWeight: 700,
                border: `1px solid ${r.color}30`,
            }}>
                {r.label}
            </span>
        );
    };

    // ─────────────────────────────────────────────────────────
    return (
        <div className="page">

            {/* ── Header ── */}
            <div className="page-header">
                <div className="page-title">👥 Users</div>
                <input
                    className="search-bar"
                    placeholder="Search name, email, role…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                />
                <button className="btn btn-primary" onClick={openCreate}>
                    + Add User
                </button>
            </div>

            {/* ── Alerts ── */}
            {success && (
                <div className="alert alert-success" style={{ marginBottom: '1rem', background: '#dcfce7', color: '#166534', border: '1px solid #86efac', borderRadius: 8, padding: '10px 14px' }}>
                    ✓ {success}
                </div>
            )}
            {err && !showForm && (
                <div className="alert alert-error" style={{ marginBottom: '1rem' }}>
                    {err}
                    <button onClick={() => setErr('')} style={{ marginLeft: 8, background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>✕</button>
                </div>
            )}

            {/* ── Stats bar ── */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.2rem', flexWrap: 'wrap' }}>
                {Object.entries(ROLE_MAP).map(([k, v]) => {
                    const count = users.filter(u => String(u.ryts) === k).length;
                    return (
                        <div key={k} style={{ background: v.bg, border: `1px solid ${v.color}30`, borderRadius: 10, padding: '8px 16px', minWidth: 120 }}>
                            <div style={{ fontSize: '.72rem', color: v.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>{v.label}</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: 800, color: v.color }}>{count}</div>
                        </div>
                    );
                })}
                <div style={{ background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 10, padding: '8px 16px', minWidth: 120 }}>
                    <div style={{ fontSize: '.72rem', color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em' }}>Inactive</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 800, color: '#64748b' }}>
                        {users.filter(u => u.active == 0).length}
                    </div>
                </div>
            </div>

            {/* ── Table ── */}
            {busy ? (
                <div className="spinner-wrap"><div className="spinner" /></div>
            ) : filtered.length === 0 ? (
                <div className="empty">No users found</div>
            ) : (
                <div className="tbl-wrap">
                    <table className="tbl">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>Name</th>
                                <th>Email</th>
                                <th>Phone</th>
                                <th>Designation</th>
                                <th>Role</th>
                                <th>Last Login</th>
                                <th>Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map(u => {
                                const isInactive = u.active == 0;
                                return (
                                    <tr key={u.id} style={{ opacity: isInactive ? 0.55 : 1 }}>
                                        <td style={{ color: 'var(--muted)', fontSize: '.82rem' }}>{u.id}</td>
                                        <td>
                                            <div style={{ fontWeight: 600 }}>{u.name}</div>
                                            {u.id == user.id && (
                                                <span style={{ fontSize: '.68rem', color: '#1a3a6e', background: '#dbeafe', borderRadius: 4, padding: '1px 5px' }}>You</span>
                                            )}
                                        </td>
                                        <td style={{ fontSize: '.85rem' }}>{u.email}</td>
                                        <td>{u.location_name && <span style={{ fontSize: '.78rem', color: 'var(--faint)' }}>🏢 {u.location_name}</span>}</td>

                                        <td style={{ fontSize: '.85rem', color: 'var(--muted)' }}>{u.phone || '—'}</td>
                                        <td style={{ fontSize: '.82rem' }}>{u.designation || '—'}</td>
                                        <td><Badge ryts={u.ryts} /></td>
                                        <td style={{ fontSize: '.78rem', color: 'var(--muted)' }}>
                                            {u.lastLogin && u.lastLogin !== '0000-00-00 00:00:00'
                                                ? new Date(u.lastLogin).toLocaleDateString()
                                                : 'Never'}
                                        </td>
                                        <td>
                                            <span style={{
                                                fontSize: '.75rem', fontWeight: 600,
                                                color: isInactive ? '#dc2626' : '#166534',
                                                background: isInactive ? '#fee2e2' : '#dcfce7',
                                                padding: '2px 8px', borderRadius: 10,
                                                border: `1px solid ${isInactive ? '#fca5a5' : '#86efac'}`,
                                            }}>
                                                {isInactive ? 'Inactive' : 'Active'}
                                            </span>
                                        </td>
                                        <td>
                                            <div style={{ display: 'flex', gap: '.3rem' }}>
                                                {/* Edit */}
                                                <button
                                                    className="btn btn-sm btn-ghost"
                                                    onClick={() => openEdit(u)}
                                                    title="Edit user"
                                                >
                                                    ✏️
                                                </button>

                                                {/* Reset Password */}
                                                <button
                                                    className="btn btn-sm"
                                                    style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}
                                                    onClick={() => { setShowPwdFor(u.id); setNewPwd(''); setErr(''); }}
                                                    title="Reset password"
                                                    disabled={u.id == user.id}
                                                >
                                                    🔑
                                                </button>

                                                {/* Deactivate / Reactivate — can't act on self or other admins */}
                                                {u.id != user.id && (
                                                    <button
                                                        className="btn btn-sm"
                                                        style={{
                                                            background: isInactive ? '#dcfce7' : '#fee2e2',
                                                            color:      isInactive ? '#166534' : '#dc2626',
                                                            border:     `1px solid ${isInactive ? '#86efac' : '#fca5a5'}`,
                                                        }}
                                                        onClick={() => toggleActive(u)}
                                                        title={isInactive ? 'Reactivate' : 'Deactivate'}
                                                    >
                                                        {isInactive ? '✓ Activate' : '⊘ Deactivate'}
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* ══════════════════════════════════════════════════
                CREATE / EDIT MODAL
            ══════════════════════════════════════════════════ */}
            {showForm && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1000, padding: '1rem',
                }}>
                    <div style={{
                        background: 'var(--card-bg, #fff)', borderRadius: 14,
                        width: '100%', maxWidth: 560,
                        boxShadow: '0 20px 60px rgba(0,0,0,.3)',
                        maxHeight: '90vh', overflowY: 'auto',
                    }}>
                        {/* Modal header */}
                        <div style={{
                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                            padding: '1.1rem 1.4rem', borderBottom: '1px solid var(--border, #e2e8f0)',
                        }}>
                            <div style={{ fontWeight: 700, fontSize: '1.05rem' }}>
                                {form.id === 0 ? '➕ Add New User' : `✏️ Edit — ${form.name}`}
                            </div>
                            <button
                                onClick={() => { setShowForm(false); setErr(''); }}
                                style={{ background: 'none', border: 'none', fontSize: '1.3rem', cursor: 'pointer', color: 'var(--muted)' }}
                            >
                                ✕
                            </button>
                        </div>

                        {/* Modal body */}
                        <form onSubmit={handleSave} style={{ padding: '1.2rem 1.4rem' }}>
                            {err && (
                                <div className="alert alert-error" style={{ marginBottom: '1rem', background: '#fee2e2', color: '#dc2626', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 12px', fontSize: '.875rem' }}>
                                    {err}
                                </div>
                            )}
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}><label>
                                Branch / Location
                                <select value={form.location_id} onChange={f('location_id')}>
                                    <option value="">-- Unassigned --</option>
                                    {locations.map(l => <option key={l.id} value={l.id}>{l.office}</option>)}
                                </select>
                                </label>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <label>
                                    Full Name *
                                    <input value={form.name} onChange={f('name')} placeholder="Jane Smith" required />
                                </label>
                                <label>
                                    Phone
                                    <input value={form.phone} onChange={f('phone')} placeholder="9876543210" type="tel" />
                                </label>
                            </div>

                            <label style={{ marginBottom: '1rem' }}>
                                Email Address *
                                <input value={form.email} onChange={f('email')} placeholder="jane@company.com" type="email" required />
                            </label>

                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                                <label>
                                    Designation *
                                    <select value={form.designation} onChange={f('designation')} required>
                                        <option value="">— Select —</option>
                                        {DESIGNATIONS.map(d => (
                                            <option key={d} value={d}>{d}</option>
                                        ))}
                                    </select>
                                </label>
                                <label>
                                    Role *
                                    <select value={form.ryts} onChange={f('ryts')} required>
                                        <option value="2">Quotation Builder</option>
                                        <option value="3">Manager</option>
                                        <option value="1">Admin / Leader</option>
                                    </select>
                                </label>
                            </div>

                            {/* Password — required on create, optional on edit */}
                            <div style={{ background: 'var(--bg, #f8fafc)', borderRadius: 8, padding: '.9rem', marginBottom: '1rem', border: '1px dashed var(--border, #e2e8f0)' }}>
                                <div style={{ fontSize: '.78rem', color: 'var(--muted)', marginBottom: '.5rem', fontWeight: 600 }}>
                                    {form.id === 0 ? 'Set Password *' : 'Change Password (leave blank to keep current)'}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '.75rem' }}>
                                    <label style={{ marginBottom: 0 }}>
                                        Password {form.id === 0 ? '*' : ''}
                                        <input
                                            type="password"
                                            value={form.password}
                                            onChange={f('password')}
                                            placeholder="Min 6 characters"
                                            required={form.id === 0}
                                            minLength={form.id === 0 ? 6 : 0}
                                        />
                                    </label>
                                    <label style={{ marginBottom: 0 }}>
                                        Confirm Password {form.id === 0 ? '*' : ''}
                                        <input
                                            type="password"
                                            value={form.confirmPassword}
                                            onChange={f('confirmPassword')}
                                            placeholder="Repeat password"
                                            required={form.id === 0}
                                        />
                                    </label>
                                </div>
                            </div>

                            <div style={{ display: 'flex', gap: '.75rem', justifyContent: 'flex-end' }}>
                                <button
                                    type="button"
                                    className="btn btn-ghost"
                                    onClick={() => { setShowForm(false); setErr(''); }}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={saving}
                                >
                                    {saving
                                        ? (form.id === 0 ? 'Creating…' : 'Saving…')
                                        : (form.id === 0 ? 'Create User' : 'Save Changes')
                                    }
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* ══════════════════════════════════════════════════
                RESET PASSWORD MODAL
            ══════════════════════════════════════════════════ */}
            {showPwdFor && (
                <div style={{
                    position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    zIndex: 1000, padding: '1rem',
                }}>
                    <div style={{
                        background: 'var(--card-bg, #fff)', borderRadius: 14,
                        width: '100%', maxWidth: 380,
                        boxShadow: '0 20px 60px rgba(0,0,0,.3)',
                        padding: '1.4rem',
                    }}>
                        <div style={{ fontWeight: 700, fontSize: '1rem', marginBottom: '1rem' }}>
                            🔑 Reset Password
                        </div>
                        {err && (
                            <div style={{ color: '#dc2626', background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 7, padding: '7px 10px', fontSize: '.85rem', marginBottom: '.8rem' }}>
                                {err}
                            </div>
                        )}
                        <label style={{ marginBottom: '1rem' }}>
                            New Password
                            <input
                                type="password"
                                value={newPwd}
                                onChange={e => setNewPwd(e.target.value)}
                                placeholder="Min 6 characters"
                                autoFocus
                            />
                        </label>
                        <div style={{ display: 'flex', gap: '.75rem', justifyContent: 'flex-end' }}>
                            <button
                                className="btn btn-ghost"
                                onClick={() => { setShowPwdFor(null); setNewPwd(''); setErr(''); }}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handlePwdReset}
                                disabled={pwdSaving}
                            >
                                {pwdSaving ? 'Resetting…' : 'Reset Password'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
}
